// Loading splash — a neutral overlay that masks the MapLibre canvas during
// FuseSimple initialization.
//
// When FuseSimple mounts, the MapLibre canvas renders its default (wrong)
// viewport briefly before the Esri → MapLibre sync push and tile loads
// complete. That brief flash looks like a broken map. The splash covers the
// basemap container with a neutral background + small label until MapLibre
// reaches its `idle` state (all tiles loaded, camera settled), then fades
// out and removes itself.
//
// Lifecycle (owned by this module):
//   - Insert: `createSplash(container, options, logger)` appends an
//     absolutely-positioned <div> as a child of the basemap container.
//     Starts a timeout timer as a safeguard.
//   - Dismiss: fade `opacity` to 0 over `fadeDuration` ms, then remove the
//     element on `transitionend` (with a belt-and-suspenders timer in case
//     `transitionend` never fires).
//   - Destroy: immediate remove — used when `FuseSimple.destroy()` runs
//     before the splash has dismissed normally.
//
// The splash module does not know about MapLibre or Esri. `FuseSimple.create()`
// subscribes to the basemap adapter's `onceIdle` and calls `splash.dismiss()`
// from there; the timeout safeguard is the splash's own concern.

import type { DebugLogger } from "../debug-logger";

export interface SplashOptions {
  /** Enable or disable the splash entirely. Default: `true`. */
  enabled?: boolean;
  /** CSS background for the splash div. Default: `"#f5f5f5"`. */
  background?: string;
  /** Centered label shown on the splash. Default: `"FuseSimple"`. */
  text?: string;
  /** Opacity of the label (0–1). Default: `0.4`. */
  textOpacity?: number;
  /** Fade-out duration in ms. `0` for instant removal. Default: `350`. */
  fadeDuration?: number;
  /** Max time in ms before the splash is force-removed. Default: `10000`. */
  timeout?: number;
}

export interface SplashHandle {
  /**
   * Begin the fade-out and remove from the DOM. Idempotent — safe to call
   * multiple times or after `destroy()`. Normally invoked by
   * `FuseSimple.create()` when the basemap adapter reports `idle`.
   */
  dismiss(): void;
  /**
   * Remove immediately without fade. Used by `FuseSimple.destroy()` when
   * the consumer tears down mid-init. Idempotent.
   */
  destroy(): void;
}

const DEFAULTS: Required<SplashOptions> = {
  enabled: true,
  background: "#f5f5f5",
  text: "FuseSimple",
  textOpacity: 0.4,
  fadeDuration: 350,
  timeout: 10000,
};

/** No-op splash handle — returned when splash is disabled. */
const NOOP_HANDLE: SplashHandle = {
  dismiss: () => {},
  destroy: () => {},
};

/**
 * Create and mount the loading splash into `container`. Returns a handle
 * the caller uses to dismiss it once the basemap is ready (or to destroy
 * it during teardown).
 */
export function createSplash(
  container: HTMLElement,
  options: SplashOptions | undefined,
  logger: DebugLogger,
): SplashHandle {
  const opts = { ...DEFAULTS, ...(options ?? {}) };

  if (!opts.enabled) {
    logger.log("LIFECYCLE", { message: "splash disabled" });
    return NOOP_HANDLE;
  }

  const el = document.createElement("div");
  el.setAttribute("data-fusesimple-splash", "");
  // The basemap container itself has `pointer-events: none`, so pointer
  // events pass through to Esri regardless. Setting it explicitly on the
  // splash keeps intent obvious if the container ever changes.
  Object.assign(el.style, {
    position: "absolute",
    inset: "0",
    background: opts.background,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "1",
    pointerEvents: "none",
    opacity: "1",
    transition: `opacity ${opts.fadeDuration}ms ease-out`,
    fontFamily: "system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', sans-serif",
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement("div");
  label.textContent = opts.text;
  // Font size scales with viewport via CSS `clamp(min, preferred, max)`:
  //   ~18px on phones, ~21px on tablets, capping at 32px on large desktops.
  // The `vw` unit means we don't need media queries, and browsers handle
  // resize smoothly. Tune the three values if the branding changes.
  Object.assign(label.style, {
    opacity: String(opts.textOpacity),
    color: "#333",
    fontSize: "clamp(18px, 2.6vw, 32px)",
    fontWeight: "500",
    letterSpacing: "0.02em",
    userSelect: "none",
  } as Partial<CSSStyleDeclaration>);
  el.appendChild(label);

  container.appendChild(el);
  logger.log("LIFECYCLE", {
    message: "splash mounted",
    text: opts.text,
    background: opts.background,
  });

  let dismissed = false;

  const timeoutId = window.setTimeout(() => {
    if (dismissed) return;
    logger.log("BUG", {
      bugId: "splash-timeout",
      category: "LIFECYCLE",
      description: `Loading splash removed by timeout — MapLibre did not reach idle state within ${opts.timeout}ms.`,
    });
    dismiss();
  }, opts.timeout);

  function removeNow(): void {
    if (el.parentNode) el.parentNode.removeChild(el);
  }

  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timeoutId);

    if (opts.fadeDuration <= 0) {
      removeNow();
      logger.log("LIFECYCLE", { message: "splash dismissed (no fade)" });
      return;
    }

    el.style.opacity = "0";
    // Belt-and-suspenders: if `transitionend` never fires (tab backgrounded,
    // transition cancelled by a style rewrite, etc.), the timer guarantees
    // removal. 50ms slack covers rAF drift.
    const fallbackId = window.setTimeout(() => {
      removeNow();
    }, opts.fadeDuration + 50);
    el.addEventListener(
      "transitionend",
      () => {
        window.clearTimeout(fallbackId);
        removeNow();
      },
      { once: true },
    );

    logger.log("LIFECYCLE", {
      message: "splash dismissed",
      fadeDuration: opts.fadeDuration,
    });
  }

  function destroy(): void {
    if (!dismissed) {
      dismissed = true;
      window.clearTimeout(timeoutId);
    }
    removeNow();
    logger.log("LIFECYCLE", { message: "splash destroyed" });
  }

  return { dismiss, destroy };
}
